export interface CartFunnelPageBodyItem {
  readonly block: string;
}

export type CartFunnelStepsSyncPlan =
  | { readonly action: 'noop'; readonly nextSyncedKey: string }
  | { readonly action: 'skip'; readonly nextSyncedKey: string }
  | { readonly action: 'clear'; readonly nextSyncedKey: string }
  | {
      readonly action: 'sync';
      readonly stepIds: readonly string[];
      readonly resetFirst: boolean;
      readonly nextSyncedKey: string;
    };

export interface PlanCartFunnelStepsSyncInput {
  readonly stepIds: readonly string[];
  readonly previouslySyncedKey: string | null;
  readonly isSharedOrder: boolean;
  readonly isPreview: boolean;
}

/**
 * Decides how the cart-funnel page should project `body` steps into the
 * cart-funnel NgRx store. Soft-refresh can populate steps after an empty
 * preview boot — the page must re-sync instead of only running once in
 * `ngAfterViewInit`.
 */
export function planCartFunnelStepsSync(
  input: PlanCartFunnelStepsSyncInput,
): CartFunnelStepsSyncPlan {
  const nextSyncedKey = input.stepIds.join('|');

  if (input.isSharedOrder) {
    return { action: 'skip', nextSyncedKey };
  }

  if (nextSyncedKey === input.previouslySyncedKey) {
    return { action: 'noop', nextSyncedKey };
  }

  if (input.stepIds.length === 0) {
    if (input.isPreview) {
      return input.previouslySyncedKey
        ? { action: 'clear', nextSyncedKey }
        : { action: 'noop', nextSyncedKey };
    }
    return { action: 'skip', nextSyncedKey };
  }

  return {
    action: 'sync',
    stepIds: input.stepIds,
    resetFirst: !!input.previouslySyncedKey,
    nextSyncedKey,
  };
}

export function normalizeCartFunnelPageBody(
  value: readonly unknown[] | undefined | null,
): CartFunnelPageBodyItem[] {
  if (!Array.isArray(value) || value.length === 0) {
    return [];
  }

  return value
    .map(normalizeCartFunnelPageBodyItem)
    .filter((item): item is CartFunnelPageBodyItem => item !== null);
}

function normalizeCartFunnelPageBodyItem(value: unknown): CartFunnelPageBodyItem | null {
  if (typeof value === 'string') {
    const block = value.trim();
    return block ? { block } : null;
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const candidate = value as { block?: unknown; id?: unknown };
  const block = typeof candidate.block === 'string' ? candidate.block.trim() : '';
  if (block) {
    return { block };
  }

  const id = typeof candidate.id === 'string' ? candidate.id.trim() : '';
  if (id) {
    return { block: id };
  }

  return null;
}
